/*
 * Preserves the default (Search & Discovery) collection filter UI state across
 * re-renders. The vendor bundle (collection-filters-form.js) replaces the whole
 * `[data-collection-sidebar]` innerHTML on every filter change, which resets:
 *   - collapse state (`open` attr on each `[data-collapsible]` group), and
 *   - "show more" expansion (`[data-link-hidden]` items + the show-more button).
 *
 * We keep a live snapshot of that state, keyed by filter group, and re-apply it
 * after each re-render — so expanding a section or opening "show more" survives
 * selecting a value. Mirrors the behaviour already built for the Nosto facets.
 */
(function () {
  'use strict';

  var SIDEBAR = '[data-collection-sidebar]';
  var FILTERS_FORM = 'collection-filters-form';
  var GROUP = '.filter-group';
  var COLLAPSIBLE = '[data-collapsible]';
  var COLLAPSIBLE_BODY = '[data-collapsible-body]';
  var HEADING_LABEL = '.filter-group__heading-label';
  var SHOW_MORE = '[data-show-more]';
  var LINK_HIDDEN = '[data-link-hidden]';
  var HIDDEN = 'hidden';

  function groupKey(group, index) {
    var label = group.querySelector(HEADING_LABEL);
    var text = label ? label.textContent.trim() : '';
    return text || '__group_' + index;
  }

  function snapshot(sidebar) {
    var state = {};
    var groups = sidebar.querySelectorAll(GROUP);
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var collapsible = group.querySelector(COLLAPSIBLE);
      var open = collapsible ? collapsible.getAttribute('open') === 'true' : true;
      var showMore = group.querySelector(SHOW_MORE);
      // The show-more wrapper gets `hidden` once the user has expanded the list.
      var expanded = showMore ? showMore.parentElement.classList.contains(HIDDEN) : false;
      state[groupKey(group, i)] = { open: open, expanded: expanded };
    }
    return state;
  }

  function applyState(sidebar, state) {
    if (!state) return;
    var groups = sidebar.querySelectorAll(GROUP);
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var snap = state[groupKey(group, i)];
      if (!snap) continue;

      // Restore collapsed groups. Freshly rendered groups default to open
      // (initial-open), so we only ever need to close — never force open.
      if (!snap.open) {
        var collapsible = group.querySelector(COLLAPSIBLE);
        if (collapsible) {
          collapsible.removeAttribute('open');
          var body = collapsible.querySelector(COLLAPSIBLE_BODY);
          if (body) body.style.height = '0px';
        }
      }

      // Restore an expanded "show more" list (mirrors the vendor showMore()).
      if (snap.expanded) {
        var showMore = group.querySelector(SHOW_MORE);
        if (showMore) {
          var wrap = showMore.parentElement;
          wrap.classList.add(HIDDEN);
          var list = wrap.previousElementSibling;
          if (list) {
            var hiddenItems = list.querySelectorAll(LINK_HIDDEN);
            for (var j = 0; j < hiddenItems.length; j++) {
              hiddenItems[j].classList.remove(HIDDEN);
            }
          }
        }
      }
    }
  }

  function enhance(sidebar) {
    // Only the default filter: skip Nosto (it has its own persistence) and any
    // sidebar without the filters form.
    if (sidebar.querySelector('[data-nosto-facets]')) return;
    if (!sidebar.querySelector(FILTERS_FORM)) return;
    if (sidebar.dataset.nsFiltersPersist === 'true') return;
    sidebar.dataset.nsFiltersPersist = 'true';

    var lastState = snapshot(sidebar);
    var scheduled = false;

    // Keep the snapshot fresh after any interaction that toggles collapse or
    // show-more. Deferred a frame so we read the state *after* the vendor
    // handlers have run.
    sidebar.addEventListener('click', function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        lastState = snapshot(sidebar);
      });
    });

    // The vendor bundle swaps the sidebar's innerHTML on every filter change.
    // Re-apply the last known state once the new groups are in place. Restore
    // only touches attributes/classes/styles (never childList), so it won't
    // retrigger this observer.
    var observer = new MutationObserver(function (mutations) {
      var replaced = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          replaced = true;
          break;
        }
      }
      if (!replaced) return;
      if (!sidebar.querySelector(GROUP)) return;
      applyState(sidebar, lastState);
    });
    observer.observe(sidebar, { childList: true, subtree: true });
  }

  function init() {
    var sidebars = document.querySelectorAll(SIDEBAR);
    for (var i = 0; i < sidebars.length; i++) {
      enhance(sidebars[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
